import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { 
  ArrowRight, 
  Cpu, 
  Zap, 
  Shield, 
  TrendingUp,
  Settings,
  Database,
  Cloud,
  CheckCircle
} from 'lucide-react';
import { images } from '../data/mock';

const TechnologyConsultingPage = () => {
  const services = [
    {
      icon: Zap,
      title: "Digital & Agile Transformation",
      description: "Accelerate your digital journey with modern methodologies and strategic execution.",
      items: [
        "Digital strategy and modernization roadmaps",
        "Agile execution and methodology implementation",
        "Change management and organizational adoption",
        "Legacy system modernization"
      ]
    },
    {
      icon: TrendingUp,
      title: "Technology Strategy & Roadmapping",
      description: "Align technology investments with business objectives for sustainable growth.",
      items: [
        "Technology assessment and gap analysis",
        "Strategic roadmap development",
        "Architecture design and planning",
        "Technology investment optimization"
      ]
    },
    {
      icon: Settings,
      title: "High-Tech & Engineering Services",
      description: "Build robust, scalable solutions with expert engineering capabilities.",
      items: [
        "Software and platform engineering",
        "Embedded systems and IoT solutions",
        "Secure architecture design",
        "API development and integration"
      ]
    },
    {
      icon: Database,
      title: "Data & AI Engineering",
      description: "Transform data into actionable insights with intelligent engineering.",
      items: [
        "Data platform architecture",
        "Machine learning infrastructure",
        "Analytics and visualization solutions",
        "Data governance and quality frameworks"
      ]
    },
    {
      icon: Cpu,
      title: "AI Strategy & Intelligent Automation",
      description: "Harness AI to automate processes and enhance decision-making.",
      items: [
        "AI readiness assessment",
        "Automation opportunity identification",
        "Intelligent workflow design",
        "AI governance frameworks"
      ]
    },
    {
      icon: Shield,
      title: "Business Optimization & Advisory",
      description: "Optimize operations and drive efficiency across your organization.",
      items: [
        "Process optimization and reengineering",
        "Technology cost optimization",
        "Vendor management and selection",
        "Risk assessment and mitigation"
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      <SEO 
        title="Technology Consulting Services"
        description="Transform your technology landscape with TekClap's technology consulting services. Digital transformation, agile execution, AI strategy, and engineering excellence."
        keywords="technology consulting services, digital transformation, agile transformation, technology strategy, AI engineering, business optimization"
      />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#FFF4E6] to-[#FAFAF8]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#F58B23] font-medium text-sm uppercase tracking-wider mb-4 block">
                Technology Consulting
              </span>
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#1A1A1A] mb-6">
                Technology Consulting Services That Drive Transformation
              </h1>
              <p className="text-lg text-[#6B7280] leading-relaxed mb-8">
                Partner with TekClap to modernize your technology landscape, accelerate digital transformation, 
                and build a foundation for sustainable growth. Our technology consulting services combine 
                strategic vision with hands-on execution.
              </p>
              <Link to="/contact">
                <Button className="bg-[#F58B23] hover:bg-[#D97706] text-white font-medium px-8 py-6 text-base">
                  Schedule Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src={images.technologyConsulting} 
                alt="Technology Consulting" 
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#FFF4E6] rounded-full flex items-center justify-center">
                    <Cpu className="w-6 h-6 text-[#F58B23]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#1A1A1A]">25+</div>
                    <div className="text-sm text-[#6B7280]">Projects Delivered</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1A1A1A] mb-6">
              Comprehensive Technology Consulting Solutions
            </h2>
            <p className="text-lg text-[#6B7280]">
              From strategy to execution, we provide end-to-end technology consulting services 
              tailored to your business objectives.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-[#FAFAF8] rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-[#FFF4E6] rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-[#F58B23]" />
                </div>
                <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3">
                  {service.title}
                </h3>
                <p className="text-[#6B7280] mb-6">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-[#374151]">
                      <CheckCircle className="w-5 h-5 text-[#F58B23] flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6">
            Ready to Transform Your Technology Landscape?
          </h2>
          <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto">
            Let&apos;s discuss how our technology consulting services can accelerate your digital transformation 
            and drive measurable business outcomes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#F58B23] hover:bg-[#D97706] text-white font-medium px-8 py-6 text-base">
                Schedule Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/client-impact">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#1A1A1A] font-medium px-8 py-6 text-base">
                View Client Impact
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <h2 className="text-2xl font-serif font-bold text-[#1A1A1A] mb-8">
            Explore Related Services
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/ai-consulting" className="group p-6 bg-[#FAFAF8] rounded-xl hover:shadow-md transition-all">
              <h3 className="font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#F58B23] transition-colors">
                AI Consulting →
              </h3>
              <p className="text-sm text-[#6B7280]">
                AI strategy, intelligent automation, and AI-enabled workflows.
              </p>
            </Link>
            <Link to="/product-development" className="group p-6 bg-[#FAFAF8] rounded-xl hover:shadow-md transition-all">
              <h3 className="font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#F58B23] transition-colors">
                Product Development →
              </h3>
              <p className="text-sm text-[#6B7280]">
                Product strategy, cloud-native SaaS, and custom engineering.
              </p>
            </Link>
            <Link to="/workforce-solutions" className="group p-6 bg-[#FAFAF8] rounded-xl hover:shadow-md transition-all">
              <h3 className="font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#F58B23] transition-colors">
                Workforce Solutions →
              </h3>
              <p className="text-sm text-[#6B7280]">
                Strategic staffing, RPO, and workforce modernization.
              </p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TechnologyConsultingPage;
